// Argus — ServicesWindow: services list + start/stop/restart via WS direct.
// Bloc task-manager-services 2026-05-07.

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { TableVirtuoso } from 'react-virtuoso';
import { wsClient } from '../lib/wsClient';
import { dayjs } from '../lib/dayjsSetup';

interface ServiceRow {
  name: string;
  displayName: string;
  status: string;
  startType: string;
  account: string;
  description: string | null;
}

type SortKey = 'name' | 'displayName' | 'status' | 'startType';
type SortDir = 'asc' | 'desc';
type Action = 'start' | 'stop' | 'restart';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  running: 'success',
  stopped: 'default',
  starting: 'info',
  stopping: 'warning',
  paused: 'warning',
  continue_pending: 'info',
  pause_pending: 'warning',
  unknown: 'default',
};

export function ServicesWindow({ agentId, agentOnline }: { agentId: string; agentOnline: boolean }): JSX.Element {
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('displayName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; svc: ServiceRow } | null>(null);
  const [confirm, setConfirm] = useState<{ svc: ServiceRow; action: Action } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const loadServices = async (): Promise<void> => {
    if (!agentOnline) {
      setError('Agent offline.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await wsClient.requestServicesList(agentId);
      setServices(result.services as ServiceRow[]);
      setCapturedAt(result.capturedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load services.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, agentOnline]);

  const handleSort = (key: SortKey): void => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    const f = filter.toLowerCase().trim();
    let arr = services;
    if (f) {
      arr = arr.filter(
        (s) => s.name.toLowerCase().includes(f) ||
          s.displayName.toLowerCase().includes(f) ||
          (s.description?.toLowerCase().includes(f) ?? false),
      );
    }
    const sorted = [...arr].sort((a, b) => {
      const sign = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'name': return a.name.localeCompare(b.name) * sign;
        case 'displayName': return a.displayName.localeCompare(b.displayName) * sign;
        case 'status': return a.status.localeCompare(b.status) * sign;
        case 'startType': return a.startType.localeCompare(b.startType) * sign;
      }
    });
    return sorted;
  }, [services, filter, sortKey, sortDir]);

  const handleAction = async (): Promise<void> => {
    if (!confirm) return;
    const { svc, action } = confirm;
    setBusy(true);
    setActionError(null);
    try {
      await wsClient.requestServiceControl(agentId, svc.name, action);
      setConfirm(null);
      setToast({ severity: 'success', message: `Service ${svc.displayName}: ${action} OK` });
      await loadServices();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed.';
      setActionError(msg);
      setToast({ severity: 'error', message: `${action} ${svc.displayName}: ${msg}` });
    } finally {
      setBusy(false);
    }
  };

  const sortIndicator = (key: SortKey): string => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Filter by name / display / description..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 360 }}
          slotProps={{
            input: {
              endAdornment: filter !== '' ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilter('')} aria-label="Clear filter" edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: 'right' }}>
          {filtered.length} / {services.length} services
          {capturedAt && ` · captured ${dayjs(capturedAt).fromNow()}`}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => void loadServices()} disabled={loading || !agentOnline}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <TableVirtuoso
          data={filtered}
          fixedHeaderContent={() => (
            <tr style={{ background: 'var(--mui-palette-background-paper)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)' }} onClick={() => handleSort('displayName')}>Display name{sortIndicator('displayName')}</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 200 }} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 110 }} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 90 }} onClick={() => handleSort('startType')}>Start{sortIndicator('startType')}</th>
              <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid var(--mui-palette-divider)', width: 60 }}></th>
            </tr>
          )}
          itemContent={(_idx, s) => (
            <>
              <td style={{ padding: '4px 8px' }}>
                <div>{s.displayName}</div>
                {s.description && (
                  <div style={{ fontSize: '0.75em', color: 'var(--mui-palette-text-disabled)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480 }}>
                    {s.description}
                  </div>
                )}
              </td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: '0.85em' }}>{s.name}</td>
              <td style={{ padding: '4px 8px' }}>
                <Chip label={s.status} size="small" color={STATUS_COLOR[s.status] ?? 'default'} sx={{ minWidth: 80 }} />
              </td>
              <td style={{ padding: '4px 8px', color: 'var(--mui-palette-text-secondary)' }}>{s.startType}</td>
              <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                <IconButton size="small" onClick={(e) => setActionMenu({ anchor: e.currentTarget, svc: s })} disabled={!agentOnline}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </td>
            </>
          )}
        />
      </Box>

      <Menu
        anchorEl={actionMenu?.anchor ?? null}
        open={actionMenu !== null}
        onClose={() => setActionMenu(null)}
      >
        <MenuItem
          disabled={actionMenu?.svc.status === 'running' || actionMenu?.svc.status === 'starting'}
          onClick={() => { if (actionMenu) { setConfirm({ svc: actionMenu.svc, action: 'start' }); setActionMenu(null); } }}
        >
          <ListItemIcon><PlayArrowIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>Start</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={actionMenu?.svc.status === 'stopped' || actionMenu?.svc.status === 'stopping'}
          onClick={() => { if (actionMenu) { setConfirm({ svc: actionMenu.svc, action: 'stop' }); setActionMenu(null); } }}
        >
          <ListItemIcon><StopIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Stop</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={actionMenu?.svc.status !== 'running'}
          onClick={() => { if (actionMenu) { setConfirm({ svc: actionMenu.svc, action: 'restart' }); setActionMenu(null); } }}
        >
          <ListItemIcon><RestartAltIcon fontSize="small" color="info" /></ListItemIcon>
          <ListItemText>Restart</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={confirm !== null} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm ? `${confirm.action.charAt(0).toUpperCase()}${confirm.action.slice(1)} service` : ''}</DialogTitle>
        <DialogContent>
          {confirm && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Are you sure you want to <strong>{confirm.action}</strong> this service?
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                <Typography variant="body2"><strong>Display name:</strong> {confirm.svc.displayName}</Typography>
                <Typography variant="body2"><strong>Service name:</strong> {confirm.svc.name}</Typography>
                <Typography variant="body2"><strong>Current status:</strong> {confirm.svc.status}</Typography>
                {confirm.svc.account && (
                  <Typography variant="body2"><strong>Account:</strong> {confirm.svc.account}</Typography>
                )}
              </Box>
              {actionError && <Alert severity="error" sx={{ mb: 1 }}>{actionError}</Alert>}
              <Typography variant="caption" color="text.secondary">
                Operation polls the SCM until the target state is reached (max 30s).
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button
            color={confirm?.action === 'stop' ? 'error' : confirm?.action === 'restart' ? 'info' : 'success'}
            variant="contained"
            onClick={() => void handleAction()}
            disabled={busy}
          >
            {busy ? 'Working...' : confirm?.action.charAt(0).toUpperCase() + (confirm?.action.slice(1) ?? '')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
